// Global Instructions Rule Applied!
import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Card, Descriptions, List, Spin, Typography, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { useFundDashboard } from '../../../../app_modules/funds/hooks/useFundDashboard.ts'
import { fetchFundInvestmentDetailFiltered } from '../../../../app_modules/funds/services/fundsService.ts'
import { useRecordRecent } from '../lib/useRecordRecent.ts'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { detailPath, avatarInitials } from '../lib/routes.ts'
import { text } from '../lib/format.ts'
import { compactMoneyByCode } from '../lib/money.ts'
import { RecordRow } from '../components/RecordRow.tsx'
import { NormalizedRow, RowStat } from '../lib/listDescriptors.ts'
import { LoadingState, ErrorState, Section } from '../components/ui.tsx'

// Per-company rollup of the fund's investments. Rendered with the same RecordRow card the
// Companies browse list uses (avatar + Cost/Rlzd/Unrlzd/MOIC) so the two screens look
// consistent. A single fixed dimension keeps the mobile list simple.
const MOBILE_AGGREGATION = ['company']

/**
 * Fund detail (read-only). Reuses `useFundDashboard` (slice-first + cold-load) for the
 * dashboard metrics and `fetchFundInvestmentDetailFiltered` for the investments list. The
 * first investments query after a cold backend start can time out (PG 57014); a retry
 * affordance recovers it rather than treating it as fatal.
 */
export function FundScreen(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const { detail, loading, error } = useFundDashboard(id)
  const fund = detail as any
  const currencies = useSelector((s: any) => s?.globalConfigs?.currencies)
  useRecordRecent('funds', id, fund?.funds__name, fund?.funds__committed_capital_fmt)

  const [invRows, setInvRows] = useState<any[]>([])
  const [invLoading, setInvLoading] = useState(true)
  const [invError, setInvError] = useState<string | null>(null)

  const loadInvestments = useCallback(async () => {
    if (!id) return
    setInvLoading(true)
    setInvError(null)
    const token = getAccessToken(store.getState().auth)
    const res = await fetchFundInvestmentDetailFiltered(id, MOBILE_AGGREGATION, [], [], 1, 100, token)
    setInvRows(Array.isArray(res.data) ? res.data : [])
    setInvError(res.error || null)
    setInvLoading(false)
  }, [id])

  useEffect(() => {
    void loadInvestments()
  }, [loadInvestments])

  // Map an investment-detail row -> the same NormalizedRow shape the Companies card uses.
  // The fund's own headline (above) stays full digits; the per-company list shows compact
  // units. Rows carry raw cost/proceeds/fmv + currency_code, so resolve the symbol by code.
  const toInvRow = useCallback(
    (row: any): NormalizedRow => {
      const code = typeof row.currency_code === 'string' ? row.currency_code : null
      const stats: RowStat[] = []
      if (row.cost != null) stats.push({ label: 'Cost', value: compactMoneyByCode(row.cost, code, currencies) })
      if (row.proceeds != null) stats.push({ label: 'Rlzd', value: compactMoneyByCode(row.proceeds, code, currencies) })
      if (row.fmv != null) stats.push({ label: 'Unrlzd', value: compactMoneyByCode(row.fmv, code, currencies) })
      // MOIC reads as another investment statistic, inline after Unrlzd (not a tag).
      if (row.moic_fmt) stats.push({ label: 'MOIC', value: String(row.moic_fmt) })
      return {
        id: String(row.accounts__id ?? row.accounts__name ?? ''),
        module: 'companies',
        title: text(row.accounts__name),
        avatarText: avatarInitials(row.accounts__name),
        stats: stats.length ? stats : undefined
      }
    },
    [currencies]
  )

  if (loading && !fund) return <LoadingState label="Loading fund…" />
  if (!fund) return <ErrorState message={error} />

  return (
    <div style={{ padding: 12 }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.25 }}>{text(fund.funds__name)}</div>
        <Descriptions column={1} size="small" colon={false} style={{ marginTop: 8 }} labelStyle={{ width: '45%' }}>
          {fund.funds__committed_capital_fmt ? (
            <Descriptions.Item label="Committed">{text(fund.funds__committed_capital_fmt)}</Descriptions.Item>
          ) : null}
          {fund.funds__called_capital_fmt ? (
            <Descriptions.Item label="Called">{text(fund.funds__called_capital_fmt)}</Descriptions.Item>
          ) : null}
        </Descriptions>
        {/* Drill down to the fund's investors (commitments). */}
        <div
          onClick={() => id && navigate(`/funds/${id}/investors`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            marginTop: 10,
            paddingTop: 10,
            borderTop: '1px solid #f0f0f0'
          }}
        >
          <span style={{ fontWeight: 500 }}>Investors</span>
          <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.3)' }} />
        </div>
      </Card>

      <Section title="Performance">
        <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '45%' }}>
          <Descriptions.Item label="Invested">{text(fund.cost_incurred_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Realized Value">{text(fund.proceeds_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Unrealized Value">{text(fund.fmv_fmt)}</Descriptions.Item>
          <Descriptions.Item label="MOIC">{text(fund.moic_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Gross IRR">{text(fund.irr_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Realized / invested">{text(fund.realized_invested_fmt)}</Descriptions.Item>
          <Descriptions.Item label="% exited">{text(fund.perc_exited_fmt)}</Descriptions.Item>
        </Descriptions>
      </Section>

      <Section title="Activity">
        <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '45%' }}>
          <Descriptions.Item label="Investments">{text(fund.investments_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Companies">{text(fund.companies_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Exited">{text(fund.no_exited_fmt)}</Descriptions.Item>
          <Descriptions.Item label="Avg check">{text(fund.avg_check_fmt)}</Descriptions.Item>
        </Descriptions>
      </Section>

      <Section title="Investments">
        {invLoading ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <Spin size="small" />
          </div>
        ) : invError ? (
          <div style={{ padding: '4px 0' }}>
            <Typography.Text type="secondary">Couldn't load investments. The fund grid can be slow to warm up.</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Button size="small" onClick={() => void loadInvestments()}>
                Retry
              </Button>
            </div>
          </div>
        ) : invRows.length === 0 ? (
          <Typography.Text type="secondary">No investments.</Typography.Text>
        ) : (
          <List
            size="small"
            dataSource={invRows}
            rowKey={(row: any) => String(row.accounts__id ?? row.accounts__name ?? '')}
            renderItem={(row: any) => (
              <RecordRow
                item={toInvRow(row)}
                onClick={row.accounts__id ? () => navigate(detailPath('companies', row.accounts__id)) : undefined}
              />
            )}
          />
        )}
      </Section>
    </div>
  )
}

export default FundScreen
